// src/features/business-functions/adapters/workspaceDetailAdapter.js
import { WORKSPACE_ICON_MAP } from '../constants/accountConstants';
import { CAMPAIGN_COPY } from '../constants/campaignCopy';

function inferWorkspaceType(name) {
  const value = (name || '').toLowerCase();
  if (value.includes('lead')) return 'lead_management';
  if (value.includes('sales') || value.includes('pipeline')) return 'sales_pipeline';
  if (value.includes('customer')) return 'customer_management';
  if (value.includes('report') || value.includes('analytics')) return 'reports';
  if (value.includes('task') || value.includes('activit')) return 'tasks';
  if (value.includes('document') || value.includes('contract')) return 'documents';
  return 'default';
}

function unwrapPayload(raw) {
  if (!raw) return null;
  if (raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
    return raw.data;
  }
  return raw;
}

/**
 * Transforms proposal / workspace backend DTO into normalized frontend model.
 */
export function adaptWorkspaceDetail(raw, workspaceId) {
  const payload = unwrapPayload(raw);
  if (!payload) return null;

  const name = payload.name ?? payload.proposal_name ?? payload.workspace_name ?? CAMPAIGN_COPY.defaultName;
  const typeKey = inferWorkspaceType(name);
  const iconConfig = WORKSPACE_ICON_MAP[typeKey] || WORKSPACE_ICON_MAP.default;

  return {
    id: payload.proposal_id ?? payload.id ?? workspaceId ?? '',
    name,
    description: payload.description ?? payload.comments ?? '',
    status: payload.is_disabled ? 'inactive' : 'active',
    type: typeKey,
    iconName: iconConfig.icon,
    accentKey: iconConfig.colorKey,
    tablesCount: payload.tables_count ?? 0,
    viewsCount: payload.views_count ?? 0,
    automationsCount: payload.automations_count ?? 0,
    createdAt: payload.created_on ?? payload.created_at ?? null,
    updatedAt: payload.updated_on ?? payload.updated_at ?? null,
  };
}

/**
 * Build workspace detail fallback from lead forms response when proposal API misses.
 */
export function adaptWorkspaceDetailFromForms(formsPayload, workspaceId) {
  const forms = extractLeadsForms(formsPayload);
  const firstForm = forms[0];
  const name = firstForm?.campaign_name || firstForm?.name || workspaceId || CAMPAIGN_COPY.defaultName;

  return adaptWorkspaceDetail(
    {
      proposal_id: workspaceId,
      name,
      description: firstForm?.description ?? '',
    },
    workspaceId
  );
}

/**
 * Extract lead form list from GetLeadsForm API payload.
 */
export function extractLeadsForms(raw) {
  const payload = unwrapPayload(raw);
  if (!payload) return [];

  if (Array.isArray(payload)) return payload;

  if (typeof payload === 'object') {
    return Object.values(payload).filter(
      (item) => item && typeof item === 'object' && (item.leads_form_id || item.id)
    );
  }

  return [];
}

/**
 * Transforms lead forms into workspace table cards.
 */
export function adaptLeadsFormsToTables(raw, recordCountsByFormId = {}) {
  return extractLeadsForms(raw).map((form) => {
    const formId = String(form.leads_form_id ?? form.id ?? '');
    const countMeta = recordCountsByFormId[formId] ?? {};

    return {
      id: formId,
      name: form.leads_form_name ?? form.name ?? 'Lead Form',
      description: form.description ?? '',
      recordsCount: countMeta.recordsCount ?? 0,
      updatedAt: countMeta.updatedAt ?? '',
      iconName: inferTableIconName(form.leads_form_name ?? form.name),
      formType: 'leads_form',
      campaignId: form.campaign_id ?? '',
      fieldCount: countFormFields(form),
      leadsFormItems: form.leads_form_items ?? form.data ?? {},
    };
  });
}

function countFormFields(form) {
  const items = form.leads_form_items ?? form.data ?? {};
  if (Array.isArray(items)) return items.length;
  if (typeof items === 'object') return Object.keys(items).length;
  return 0;
}

function inferTableIconName(name) {
  const value = (name || '').toLowerCase();
  if (value.includes('lead')) return 'Groups';
  if (value.includes('contact')) return 'PersonOutline';
  if (value.includes('deal') || value.includes('pipeline')) return 'FilterAlt';
  if (value.includes('task')) return 'TaskAlt';
  if (value.includes('activit')) return 'Assignment';
  return 'TableChart';
}

/**
 * Legacy adapter kept for compatibility — delegates to leads-form mapper.
 */
export function adaptWorkspaceTables(rawList, recordCountsByFormId = {}) {
  if (Array.isArray(rawList)) {
    return rawList.map((item) => ({
      id: item.id ?? '',
      name: item.name ?? 'Untitled Table',
      description: item.description ?? '',
      recordsCount: item.records_count ?? item.recordsCount ?? 0,
      updatedAt: item.updated_at ?? item.updatedAt ?? '',
      iconName: item.icon_name ?? 'TableChart',
      formType: item.form_type ?? 'leads_form',
    }));
  }

  return adaptLeadsFormsToTables(rawList, recordCountsByFormId);
}

export function extractLeadFormRecordMeta(raw) {
  const payload = unwrapPayload(raw);
  if (!payload || typeof payload === 'string') {
    return { recordsCount: 0, updatedAt: '' };
  }

  return {
    recordsCount: payload.total_count ?? payload.values?.length ?? 0,
    updatedAt: payload.values?.length ? 'Recently updated' : '',
  };
}
